import {Card, closeCurrentDialog, DataStatus, EmptyStatus, openDialog} from 'platform/components';
import {Box, Center, Grid, Hide, HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil, isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {
  useChangeInterestCustomerMutation,
  useSelectInterestContactPersonMutation,
  useUpdateInterestUtmMutation,
} from '@dms/api/finance';
import {EntityResourceIds, UtmParameters} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {CustomerInsightsTrigger} from '@dms/features/customer-insights';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CustomerMatchOrCreate,
  CustomerWidgetCard,
  handleApiError,
  Section,
  useInterestEditPermissions,
  usePermissions,
  UtmCard,
} from '@dms/shared';

import {useInterestData} from '../../hooks/useInterestData';
import {ContactPersonForm} from './ContactPersonForm';

interface CustomerProps {
  interestId: string;
}

export function Customer(props: CustomerProps) {
  const {utm, interest, customer, isLoading, isError} = useInterestData(props.interestId);

  const {data: interestParticipation} = useGetParticipationQuery({
    recordId: props.interestId,
    resourceId: EntityResourceIds.interest,
  });
  const [changeCustomer] = useChangeInterestCustomerMutation();
  const [selectContactPerson] = useSelectInterestContactPersonMutation();
  const [updateInterestUtm] = useUpdateInterestUtmMutation();

  const [hasChangeBusinessCaseCustomerPermission] = usePermissions({
    permissionKeys: ['changeBusinessCaseCustomer'],
    scopes: {
      changeBusinessCaseCustomer: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
    },
  });

  const {hasChangeCustomerInterestPermission, hasSelectContactPersonInterestPermission} =
    useInterestEditPermissions({
      interestId: props.interestId,
    });

  const onCustomer = (customer: CustomerResponseBodyV2) =>
    changeCustomer({
      interestId: props.interestId,
      changeInterestCustomerRequestBody: {
        customerId: customer.id,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const handleCustomerChange = () =>
    openDialog(
      <CustomerMatchOrCreate
        data-testid={testIds.interest.detail('customer')}
        onCustomer={onCustomer}
      />,
      {title: i18n.t('entity.customer.labels.customer')}
    );

  const handleUtmSubmit = (values: UtmParameters) =>
    updateInterestUtm({
      id: props.interestId,
      updateInterestUtmRequestBody: values,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const isInterestClosed = interest?.state === 'CLOSED' || interest?.state === 'UNSUCCESSFUL';

  const [showContactPersonForm, setShowContactPersonForm] = useState(false);

  const customerId = customer?.id;
  const contactPersonId = interest?.contactPersonId;

  const isContactPersonDisabled = isInterestClosed || !hasSelectContactPersonInterestPermission;

  const toggleContactPerson = (value: boolean) => {
    if (value) {
      setShowContactPersonForm(true);
      return;
    }

    selectContactPerson({
      id: props.interestId,
      selectInterestContactPersonRequestBody: {
        contactPersonId: null,
      },
    })
      .unwrap()
      .then(() => setShowContactPersonForm(false))
      .catch(handleApiError);
  };

  return (
    <Section>
      <Show when={isNilOrEmpty(customer)}>
        <HStack justify="center">
          <Box width={188}>
            <CustomerWidgetCard
              onCustomer={onCustomer}
              secondStepComponentType="BUSINESS_CASE"
              shouldWarnBeforeActiveBusinessCaseEdit
              data-testid={testIds.interest.detail('customer')}
              canChangeCustomer={hasChangeCustomerInterestPermission}
            />
          </Box>
        </HStack>
      </Show>

      <Show when={isNotNil(customer)}>
        <Grid columns={[1, 2]} align="flex-start">
          <VStack spacing={4}>
            <Show when={customer}>
              <CustomerWidgetCard
                resourceId={EntityResourceIds.interest}
                recordId={props.interestId}
                isContactPersonHidden
                customerId={customer?.id}
                onCustomer={onCustomer}
                onChange={handleCustomerChange}
                secondStepComponentType="BUSINESS_CASE"
                shouldWarnBeforeActiveBusinessCaseEdit
                data-testid={testIds.interest.detail('customer')}
                isDetailLinkVisible
                headerAction={
                  <CustomerInsightsTrigger
                    customerId={customerId}
                    data-testid="interest-customerInsights"
                  />
                }
                canChangeCustomer={
                  hasChangeBusinessCaseCustomerPermission &&
                  !isInterestClosed &&
                  hasChangeCustomerInterestPermission
                }
              />
            </Show>

            <Hide when={customer}>
              <Card title={i18n.t('entity.customer.labels.customer')}>
                <DataStatus isLoading={isLoading} isError={isError} minHeight={CARD_MIN_HEIGHT}>
                  <Box minHeight={CARD_MIN_HEIGHT}>
                    <Center height={CARD_MIN_HEIGHT}>
                      <EmptyStatus data-testid={testIds.interest.detail('empty-customer')} />
                    </Center>
                  </Box>
                </DataStatus>
              </Card>
            </Hide>

            <Show whenFeatureEnabled={featureFlags.INTERESTS_UTM}>
              <UtmCard
                parameters={utm}
                onSubmit={handleUtmSubmit}
                data-testid={testIds.interest.detail('customer-utm')}
                isDisabled={isInterestClosed}
              />
            </Show>
          </VStack>

          <Card
            data-testid={testIds.interest.detail('contactPerson')}
            variant="default"
            isExpanded={isNotNil(contactPersonId) || showContactPersonForm}
            control={{
              type: 'switch',
              value: isNotNil(contactPersonId) || showContactPersonForm,
              isDisabled: isContactPersonDisabled,
              onChange: toggleContactPerson,
              'data-testid': testIds.interest.detail('toggleContactPerson'),
            }}
            title={i18n.t('entity.businessCase.labels.contactPerson')}
          >
            <Card data-testid={testIds.interest.detail('contactPersonInner')} variant="inlineGrey">
              <Show when={customerId}>
                <ContactPersonForm
                  interestId={props.interestId}
                  customerId={customerId ?? ''}
                  contactPersonId={contactPersonId}
                  isDisabled={isContactPersonDisabled}
                />
              </Show>
            </Card>
          </Card>
        </Grid>
      </Show>
    </Section>
  );
}

const CARD_MIN_HEIGHT = 100;
